/**
 * Cross-subdomain "logout everywhere" signal — the Atlassian/Jira model
 * (a central-account logout invalidates every site), rather than Slack's
 * per-workspace-only logout.
 *
 * localStorage AND Auth0's own SDK session cache are both scoped per
 * ORIGIN, so logging out on {slugA}.{rootDomain} does not, by itself,
 * invalidate a session already cached on {slugB}.{rootDomain} or the bare
 * root domain — each origin keeps trusting its own local cache until its
 * token naturally expires (up to 15 minutes). Cookies, unlike localStorage,
 * CAN be shared across subdomains via `Domain=.{rootDomain}` — so logout
 * writes a small timestamp marker there, and every origin compares its own
 * cached session's issued-at time against it on boot (see useAuth.js).
 */

import { getRootDomain } from './subdomain.js';

const LOGOUT_MARKER_COOKIE = 'aims_logged_out_at';
// Only needs to outlive the JWT's own TTL (15 min) — once every origin's
// natural token expiry has passed anyway, the marker has done its job.
const LOGOUT_MARKER_MAX_AGE_SECONDS = 30 * 60;

function getCookieDomain(rootDomain = getRootDomain()) {
  return rootDomain ? `.${rootDomain}` : '.localhost';
}

/**
 * Write the "logged out everywhere" marker. Call this as part of logout,
 * before redirecting to Auth0's /v2/logout.
 */
export function markGlobalLogout() {
  if (typeof document === 'undefined') return;
  const domain = getCookieDomain();
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:'
    ? '; Secure'
    : '';
  document.cookie =
    `${LOGOUT_MARKER_COOKIE}=${Date.now()}; Domain=${domain}; Path=/; ` +
    `Max-Age=${LOGOUT_MARKER_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
}

function readLogoutMarkerMs() {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${LOGOUT_MARKER_COOKIE}=([^;]+)`),
  );
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isFinite(value) ? value : null;
}

/**
 * True when a global-logout marker exists that's newer than
 * `sessionIssuedAtSeconds` (a JWT `iat`) — meaning this origin's locally
 * cached session predates the last "logout everywhere" and must be treated
 * as stale, even though neither Auth0's own SDK cache nor our JWT has
 * actually expired yet.
 *
 * @param {number|null|undefined} sessionIssuedAtSeconds - JWT `iat` (seconds)
 * @returns {boolean}
 */
export function isSessionStaleAfterGlobalLogout(sessionIssuedAtSeconds) {
  const markerMs = readLogoutMarkerMs();
  if (markerMs == null) return false;
  // No iat to compare against (e.g. no cached token at all) — nothing to
  // invalidate, so don't force a false positive.
  if (!sessionIssuedAtSeconds) return false;
  return markerMs > sessionIssuedAtSeconds * 1000;
}
